import { FC, Fragment } from "react";
import { Menu, Tooltip, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import { LiveUsageResourceGridToolbarCleanActions } from "./clean/LiveUsageResourceGridToolbarCleanActions";
import { LiveUsageResourceGridProtectAction } from "./LiveUsageResourceGridProtectAction";
import { ResourceType } from "@/services/cloudchipr.api";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { useAppSelector } from "@/store/hooks";
import { differentResourceTypesSelectedOnOneGrid } from "@/store/resources-grid/selectors/grid/selection/differentResourceTypesSelectedOnOneGrid";
import { selectedResourcesLengthByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/checkers/selectedResourcesLengthByMasterIdSelector";
import { liveUsageResourceActionOptionsByVisibilityExistsSelector } from "@/store/live-usage-mgmt/selectors/actions/liveUsageResourceActionOptionsByVisibilityExistsSelector";
import { selectedResourcesAccountAccessTypesSelector } from "@/store/resources-grid/selectors/grid/selection/account/selectedResourcesAccountAccessTypesSelector";

interface LiveUsageResourceGridToolbarDropDownActionsProps {
  masterResourceType: ResourceType;
  masterGridId: string;
}

export const LiveUsageResourceGridToolbarDropDownActions: FC<
  LiveUsageResourceGridToolbarDropDownActionsProps
> = ({ masterResourceType, masterGridId }) => {
  const differentResourceTypesSelected = useAppSelector((state) =>
    differentResourceTypesSelectedOnOneGrid(state, masterGridId),
  );
  const selectedResourcesAccountAccessType = useAppSelector((state) =>
    selectedResourcesAccountAccessTypesSelector(state, masterGridId),
  );

  const selectedResourcesLength = useAppSelector((state) =>
    selectedResourcesLengthByMasterIdSelector(state, masterGridId),
  );
  const withActions = useAppSelector((state) =>
    liveUsageResourceActionOptionsByVisibilityExistsSelector(
      state,
      masterGridId,
      masterResourceType,
    ),
  );

  const isSelectedResourcesAccountTypeRead =
    selectedResourcesAccountAccessType === "read";

  const disabled =
    differentResourceTypesSelected ||
    !selectedResourcesLength ||
    !withActions ||
    isSelectedResourcesAccountTypeRead;

  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  let tooltipTitle = "";
  if (differentResourceTypesSelected) {
    tooltipTitle =
      "To perform actions, please select resources of the same type.";
  }

  if (isSelectedResourcesAccountTypeRead) {
    tooltipTitle =
      "Resource actions can only be performed on Read/Write accounts. Please switch to Read/Write access mode to enable actions.";
  }

  if (!selectedResourcesLength) {
    tooltipTitle = "Select at least 1 resource to enable actions.";
  }

  if (!withActions) {
    tooltipTitle = "No Available Actions for current resource type";
  }

  return (
    <Fragment>
      <Tooltip arrow placement="top" title={tooltipTitle}>
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={openMenu}
            sx={{ pr: 1 }}
            disabled={disabled}
          >
            <Typography pr={0.5}>Actions</Typography>
            <ArrowDropDownIcon />
          </Button>
        </span>
      </Tooltip>

      <Menu anchorEl={anchor} onClose={closeMenu} open={open}>
        <LiveUsageResourceGridToolbarCleanActions
          onClose={closeMenu}
          masterGridId={masterGridId}
          masterResourceType={masterResourceType}
        />

        <LiveUsageResourceGridProtectAction
          onClose={closeMenu}
          masterGridId={masterGridId}
        />
      </Menu>
    </Fragment>
  );
};
